import type { QuestionFillGapsAnswerSchema, QuestionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { questionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

const GAP_PATTERN = /\[([^\]]+)\]/g;

export interface Segment {
  id: string;
  type: 'text' | 'gap';
  content: string;
  gapKey?: string;
}

function getNextGapId(existingGapIds: Set<string> | string[]): string {
  const gapSet = existingGapIds instanceof Set ? existingGapIds : new Set(existingGapIds);
  let counter = 1;
  let gapId = `luka ${counter}`;
  while (gapSet.has(gapId)) {
    counter++;
    gapId = `luka ${counter}`;
  }
  return gapId;
}

function segmentsToGapsContent(segments: Segment[]): string {
  let result = '';
  for (const segment of segments) {
    result += segment.type === 'gap' ? `[${segment.gapKey}]` : segment.content;
  }
  return result;
}

function parseGapsContent(content: string): Segment[] {
  if (!content) {
    return [{ id: uuidv4(), type: 'text', content: '' }];
  }

  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  GAP_PATTERN.lastIndex = 0;

  while ((match = GAP_PATTERN.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        id: uuidv4(),
        type: 'text',
        content: content.substring(lastIndex, match.index),
      });
    }

    // Add the gap
    const gapKey = match[1]!;
    segments.push({
      id: uuidv4(),
      type: 'gap',
      content: gapKey,
      gapKey,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last gap
  if (lastIndex < content.length) {
    segments.push({
      id: uuidv4(),
      type: 'text',
      content: content.substring(lastIndex),
    });
  }

  // Handle case with no gaps
  if (segments.length === 0) {
    segments.push({ id: uuidv4(), type: 'text', content });
  }

  // Ensure we end with a text segment for cursor navigation
  if (segments[segments.length - 1]?.type === 'gap') {
    segments.push({ id: uuidv4(), type: 'text', content: '' });
  }

  return segments;
}

function mergeAdjacentTextSegments(segments: Segment[]): Segment[] {
  if (segments.length === 0) return segments;

  const merged: Segment[] = [];
  let i = 0;

  while (i < segments.length) {
    const current = segments[i]!;

    if (current.type === 'gap') {
      merged.push(current);
      i++;
      continue;
    }

    // Accumulate consecutive text segments
    let textContent = current.content;
    const mergedId = current.id;
    let j = i + 1;

    while (j < segments.length && segments[j]?.type === 'text') {
      textContent += segments[j]!.content;
      j++;
    }

    merged.push({ id: mergedId, type: 'text', content: textContent });
    i = j;
  }

  return merged;
}

function extractGapIds(content: string): string[] {
  const gapIds: string[] = [];
  GAP_PATTERN.lastIndex = 0;

  for (const match of content.matchAll(GAP_PATTERN)) {
    const gapId = match[1]?.trim();
    if (gapId) {
      gapIds.push(gapId);
    }
  }

  return gapIds;
}

function createGapData(gapId: string): QuestionFillGapsAnswerSchema {
  return {
    id: gapId,
    points: 1,
    options: [
      {
        id: uuidv4(),
        content: 'opcja 1',
        correct: true,
        explanation: undefined,
      },
      {
        id: uuidv4(),
        content: 'opcja 2',
        correct: false,
        explanation: undefined,
      },
    ],
  };
}

export function useQuestionFillForm(data?: QuestionFillGapsSchema) {
  const form = useForm<QuestionFillGapsSchema>({
    defaultValues: data,
    mode: 'onSubmit',
    resolver: standardSchemaResolver(
      questionFillGapsSchema.transform((formData) => ({
        ...formData,
        points: formData.gaps.reduce((sum, gap) => sum + (gap.points || 0), 0),
      }))
    ),
  });

  const fillQuestionGaps = useFieldArray<QuestionFillGapsSchema>({
    control: form.control,
    name: 'gaps',
  });

  const [segments, setSegments] = useState<Segment[]>(() => parseGapsContent(data?.gapsContent || ''));

  const segmentsRef = useRef(segments);
  const isMountedRef = useRef(true);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (data?.gapsContent && data.gaps) {
      const gapIds = extractGapIds(data.gapsContent);
      const existingGaps = data.gaps;

      const gapsMap = new Map<string, QuestionFillGapsAnswerSchema>();
      for (const gap of existingGaps) {
        gapsMap.set(gap.id, gap);
      }

      const newGaps = gapIds.map((gapId) => gapsMap.get(gapId) || createGapData(gapId));
      form.setValue('gaps', newGaps, { shouldValidate: false });
    }
  }, []);

  const cancelDebouncedSync = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const syncGapsContentOnly = useCallback(
    (newGapsContent: string) => {
      if (!isMountedRef.current) return;
      form.setValue('gapsContent', newGapsContent, { shouldValidate: false });
    },
    [form]
  );

  const syncGapsStructure = useCallback(
    (newGapsContent: string, newGapIds: string[]) => {
      if (!isMountedRef.current) return;

      const currentGaps = form.getValues('gaps') || [];

      const gapsMap = new Map<string, QuestionFillGapsAnswerSchema>();
      for (const gap of currentGaps) {
        gapsMap.set(gap.id, gap);
      }

      const newGaps = newGapIds.map((gapId) => gapsMap.get(gapId) || createGapData(gapId));

      form.setValue('gapsContent', newGapsContent, { shouldValidate: false });
      form.setValue('gaps', newGaps, { shouldValidate: false });
    },
    [form]
  );

  const syncGapsContentDebounced = useCallback(
    (newGapsContent: string) => {
      if (!isMountedRef.current) return;

      cancelDebouncedSync();

      debounceTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        syncGapsContentOnly(newGapsContent);
        debounceTimeoutRef.current = null;
      }, 300);
    },
    [syncGapsContentOnly, cancelDebouncedSync]
  );

  const updateSegmentContent = useCallback(
    (segmentIndex: number, newContent: string) => {
      const currentSegments = segmentsRef.current;
      const targetSegment = currentSegments[segmentIndex];

      if (!targetSegment) return;

      if (targetSegment.content === newContent) return;

      const updatedSegments = [...currentSegments.slice(0, segmentIndex), { ...targetSegment, content: newContent }, ...currentSegments.slice(segmentIndex + 1)];

      segmentsRef.current = updatedSegments;

      const newGapsContent = segmentsToGapsContent(updatedSegments);
      syncGapsContentDebounced(newGapsContent);
    },
    [syncGapsContentDebounced]
  );

  const insertGapAtSegment = useCallback(
    (segmentIndex: number, beforeText: string, afterText: string): { newSegments: Segment[]; newTextSegmentIndex: number } => {
      cancelDebouncedSync();

      const currentSegments = segmentsRef.current;

      const existingGapIds = new Set<string>();
      const existingGapIdsArray: string[] = [];
      for (const segment of currentSegments) {
        if (segment.type === 'gap' && segment.gapKey) {
          existingGapIds.add(segment.gapKey);
          existingGapIdsArray.push(segment.gapKey);
        }
      }

      const newGapId = getNextGapId(existingGapIds);

      const gapSegment: Segment = {
        id: uuidv4(),
        type: 'gap',
        content: newGapId,
        gapKey: newGapId,
      };
      const afterSegment: Segment = { id: uuidv4(), type: 'text', content: afterText };

      const insertedSegments: Segment[] = beforeText ? [{ id: uuidv4(), type: 'text', content: beforeText }, gapSegment, afterSegment] : [gapSegment, afterSegment];

      const newSegments: Segment[] = [...currentSegments.slice(0, segmentIndex), ...insertedSegments, ...currentSegments.slice(segmentIndex + 1)];

      segmentsRef.current = newSegments;
      setSegments(newSegments);

      const newGapsContent = segmentsToGapsContent(newSegments);
      existingGapIdsArray.push(newGapId);
      syncGapsStructure(newGapsContent, existingGapIdsArray);

      const newTextSegmentIndex = segmentIndex + (beforeText ? 2 : 1);
      return { newSegments, newTextSegmentIndex };
    },
    [syncGapsStructure, cancelDebouncedSync]
  );

  const removeGap = useCallback(
    (
      gapKey: string
    ): {
      mergedSegments: Segment[];
      cursorSegmentInfo: { index: number; offset: number } | null;
    } => {
      cancelDebouncedSync();

      const currentSegments = segmentsRef.current;

      let removedIndex: number = -1;
      let cursorPositionInText: number = 0;

      for (let i = 0; i < currentSegments.length; i++) {
        const segment = currentSegments[i]!;
        if (segment.gapKey === gapKey) {
          removedIndex = i;
          break;
        }
        if (segment.type === 'text') {
          cursorPositionInText += segment.content.length;
        }
      }

      if (removedIndex === -1) {
        return { mergedSegments: currentSegments, cursorSegmentInfo: null };
      }

      const filteredSegments = [...currentSegments.slice(0, removedIndex), ...currentSegments.slice(removedIndex + 1)];

      const mergedSegments = mergeAdjacentTextSegments(filteredSegments);

      if (mergedSegments.length === 0) {
        mergedSegments.push({ id: uuidv4(), type: 'text', content: '' });
      }

      segmentsRef.current = mergedSegments;
      setSegments(mergedSegments);

      const newGapIds: string[] = [];
      let newGapsContent = '';
      for (const segment of mergedSegments) {
        if (segment.type === 'gap') {
          newGapsContent += `[${segment.gapKey}]`;
          if (segment.gapKey) {
            newGapIds.push(segment.gapKey);
          }
        } else {
          newGapsContent += segment.content;
        }
      }

      syncGapsStructure(newGapsContent, newGapIds);

      let accumulatedLength = 0;
      let targetSegmentIndex = -1;
      let offsetInSegment = 0;

      for (let i = 0; i < mergedSegments.length; i++) {
        const segment = mergedSegments[i]!;
        if (segment.type === 'text') {
          const segmentLength = segment.content.length;
          if (cursorPositionInText <= accumulatedLength + segmentLength) {
            targetSegmentIndex = i;
            offsetInSegment = cursorPositionInText - accumulatedLength;
            break;
          }
          accumulatedLength += segmentLength;
        }
      }

      if (targetSegmentIndex === -1) {
        for (let i = mergedSegments.length - 1; i >= 0; i--) {
          const segment = mergedSegments[i];
          if (segment?.type === 'text') {
            targetSegmentIndex = i;
            offsetInSegment = segment.content.length;
            break;
          }
        }
      }

      return {
        mergedSegments,
        cursorSegmentInfo: targetSegmentIndex !== -1 ? { index: targetSegmentIndex, offset: offsetInSegment } : null,
      };
    },
    [syncGapsStructure, cancelDebouncedSync]
  );

  const gapKeySignature = useMemo<string>(() => {
    const keys: string[] = [];
    for (const segment of segments) {
      if (segment.type === 'gap' && segment.gapKey) {
        keys.push(segment.gapKey);
      }
    }
    return keys.join('-');
  }, [segments]);

  return {
    form,
    fillQuestionGaps,
    segments,
    segmentsRef,
    gapKeySignature,
    updateSegmentContent,
    insertGapAtSegment,
    removeGap,
  };
}
